import common from '../common';
import * as aliendata from './aliendata';
import GemTile from '../traps/GemTile';
import ConveyorBelt from '../traps/ConveyorBelt';
import SpikeTrap from '../traps/SpikeTrap';
import CrateObject from './CrateObject';
import Trap from '../traps/Trap';
import ObjectTile from '../traps/ObjectTile';
import ParticleTile from '../traps/ParticleTile';
import THREEParticleEmitter from '../particles/THREEParticleEmitter';

export default class Player {
	constructor(world, t) {
		this.mesh = null;
		this.world = world;
		this.tile = t;
		this._walkDistance = 1;
		this._jumpheight = 2;
		this._stepSize = 4;
		this._walkspeed = 1;
		this._walkspeedSpecial = 1;
		this.name = 'Ben';
		this.loaded = false;
		this.moving = false;
		this.rotating = false;
		this.rolling = false;
		this.direction = Player.DIRECTION.RIGHT;
		this.invincible = false;
		this.startTile = t.position;
		this._isUnderwater = false;
		this._canSwim = false;
		this.coins = 0;
		this._pushingCrate = false;

		this.animations = {
			run  : 'walk',
			idle : 'idle',
			jump : 'jump',
			die  : 'die'
		};
		this.spineStates = {};
		this.animationData = null;
		this.currentCharacter = 'ben';
		this.commandToExecute = null;
		this.tweens = new P3.utils.TWEEN.Group();

		this.aliendata = aliendata.ben;
		this.dead = false;
		this.previousTile = null;
		this.footstep = 0;
		this.waterParticles = null;

		this.createSignals();

		this.init();
	}

	createSignals() {
		if (typeof this.signals === 'undefined') {
			this.signals = {};
		}

		this.signals.moved = new P3.utils.MiniSignal();
		this.signals.jumped = new P3.utils.MiniSignal();
		this.signals.died = new P3.utils.MiniSignal();
		this.signals.success = new P3.utils.MiniSignal();
		this.signals.rotated = new P3.utils.MiniSignal();
		this.signals.transform = new P3.utils.MiniSignal();
		this.signals.transformEffect = new P3.utils.MiniSignal();
		this.signals.coinpickup = new P3.utils.MiniSignal();
	}

	destroySignals() {
		for (const i in this.signals) {
			if (this.signals[i] instanceof P3.utils.MiniSignal) {
				this.signals[i].detachAll();
			}
		}

		this.signals = null;
	}

	init() {
		this.animationData = P3.assets.json('animations');

		const characterdata = this.animationData[this.currentCharacter];

		this.animations = characterdata.animations;
		this._walkspeed = characterdata.speed;
		this._canSwim = characterdata.canswim;
		this._walkspeedSpecial = characterdata.special_speed;
		this.name = characterdata.printname;

		this.mesh = new THREE.Group();

		this.waterParticles = new THREEParticleEmitter(P3.assets.json('particle_water_splash'));
		common.THREEstage.add(this.waterParticles);
		common.particles3D.push(this.waterParticles);

		for (const item in this.animationData) {
			const spine =
            {
            	spine : null,
            	data  : this.animationData[item]
            };

			spine.spine = common.assets.spine(this.animationData[item].filename);
			spine.spine.scale.set(this.animationData[item].scale, this.animationData[item].scale, this.animationData[item].scale);
			spine.spine.state.setAnimation(0, aliendata[item].animations.idle, true);
			spine.spine.autoUpdate = false;
			spine.spine.update(0);

			this.spineStates[item] = spine;
		}
		this.onLoad();

		const omnitrixObj = common.assets.anim('omnitrix');
		this.mesh.icon = omnitrixObj.scene;
		const skinnedMesh = this.mesh.icon.children[0].children[0];
		skinnedMesh.material = new THREE.MeshBasicMaterial({
			map      : common.assets.texture('omnitrix_ben'),
			skinning : true
		});

		this.mesh.icon.rotateX(Math.PI * 1.5);
		this.mesh.icon.position.z += 3.2;
		this.mesh.icon.scale.set(2, 2, 2);
		this.mesh.icon.visible = false;
		this.mesh.add(this.mesh.icon);
	}

	onLoad() {
		if (this.loaded) {
			return;
		}

		this.animations = aliendata[this.currentCharacter].animations;
		this.loaded = true;
		this.mesh.spine = this.spineStates.ben.spine;
		this.mesh.spine.state.setAnimation(0, this.animations.idle, true);
		this.mesh.spine.state.data.defaultMix = 0.2;
		this.mesh.spine.autoUpdate = false;
		this.mesh.spine.update(0);
		this.mesh.spine.visible = true;
		this.mesh.add(this.mesh.spine);
		common.THREEstage.add(this.mesh);
	}

	/**
	 * type can be one of
	 * `instantDeath` 	- plays no animation, stops everything, resets everything, straight back to start
	 * `death`			- plays death anim where ben falls to the ground
	 * `passive`		- plays the fail animation where ben scratches his head
	 */
	onDeath(type) {
		this.dead = true;
		if (type === 'instantDeath') {
			this.tweens.removeAll();
		}
		this._pushingCrate = false;
		this.signals.died.dispatch(type);
	}

	preMove(nextTile, cb, allowgoal = true, type, midjump = false) {
		const crates = nextTile.objects.filter((element) => {
			return element instanceof CrateObject;
		});

		if (nextTile.types.includes('gem') && !midjump) {
			P3.utils.delayg(this.tweens, (this.aliendata.walkspeed * 0.5) / 1000, () => {
				const gemTile = nextTile.objects.find((e) => {
					return e instanceof GemTile;
				});

				if (!gemTile.claimed) {
					gemTile.claim();
					this.coins++;
				}
				this.signals.coinpickup.dispatch(this);
			});
		}

		if (nextTile.types.includes('particles')) {
			const tile = nextTile.objects.find((e) => {
				return e instanceof ParticleTile;
			});

			if (tile && this.aliendata.dangerousParticles.includes(tile.jsonName) && !this.isUnderwater) {
				this.dead = true;
				setTimeout(() => {
					this.onDeath('instantDeath');
				}, this.aliendata.walkspeed * 0.25);
			}

			cb.call(this, true);

			return;
		}

		// If the next tile is a tile, underwater AND a wall its safe to assume that the wall is underwater and we have to go up.
		if (this.aliendata.invincibleToWater && this.isUnderwater && (!nextTile.isUnderwater || (nextTile.isTile && nextTile.isWall))) {
			// we rise back up
			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({
					y: nextTile.position3D.y + 0.4
				}, 1000)
				.easing(P3.utils.TWEEN.Easing.Linear.None)
				.onComplete(() => {
					this.isUnderwater = false;

					if (this.tile.types.includes('particles')) {
						const tile = this.tile.objects.find((e) => {
							return e instanceof ParticleTile;
						});

						if (tile && this.aliendata.dangerousParticles.includes(tile.jsonName)) {
							this.dead = true;
							this.onDeath('instantDeath');
							return;
						}

						cb.call(this, true);
						return;
					}

					if (nextTile.isGoal && allowgoal) {
						P3.utils.delay(0.4, () => {
							this.signals.success.dispatch(this);
						});
					}

					cb.call(this, true);
				})
				.start();

			this.mesh.spine.state.setAnimation(0, 'idle', true);
		} else if (((nextTile.isWall && !nextTile.isTile) && !this.isUnderwater) ||
            (nextTile.isWall && nextTile.isUnderwater && this.isUnderwater && !this.aliendata.invincibleToWater)) {
			cb.call(this, false);
		} else if (nextTile.isGoal && allowgoal && !this.dead) {
			if (crates.length > 0 && type !== 'jump' && this.currentCharacter !== 'cannonbolt') {
				cb.call(this, false);

				return;
			}

			// what is this ? isnt success signal handled in other place? onMove() method?
			setTimeout(() => {
				if (this.currentCharacter === 'stinkfly') {
					new Promise((resolve) => {
						this.aliendata.deactivate(this, this.world, resolve);
					}).then(() => {
						this.transform('ben', false, () => {
							this.signals.success.dispatch();
						});
					});
				} else if (this.currentCharacter !== 'ben') {
					this.transform('ben', false, () => {
						this.signals.success.dispatch();
					});
				} else {
					this.signals.success.dispatch();
				}
			}, this.aliendata.walkspeed * 0.5);

			cb.call(this, true);
		} else if (crates.length > 0) {
			for (const crate of crates) {
				if (crate && type === 'move') {
					if (this.currentCharacter !== 'ben' && this.currentCharacter !== 'fourarms') {
						cb.call(this, false);

						return;
					}
					if (crate.onConveyor || !crate.canActivate) {	// just continue if the crate is moving (e.g. on a conveyor, or already being pushed) cause there's nothing to do.
						cb.call(this, true);

						return;
					}
					let secondTile = null;

					if (this.direction.name === 'left' || this.direction.name === 'right') {
						secondTile = this.world.grid.getTileAt(nextTile.position.x + Number(this.direction.dir), nextTile.position.y);
					} else {
						secondTile = this.world.grid.getTileAt(nextTile.position.x, nextTile.position.y + Number(this.direction.dir));
					}

					if (secondTile.isBad || !secondTile.isTile || (secondTile.isWall && !secondTile.isUnderwater) ||
                        (this.currentCharacter !== 'fourarms' && this.currentCharacter !== 'cannonbolt')) {
						// what if there's a wall behind crate?
						if (this.currentCharacter === 'fourarms') {
							if (crate.onConveyor) {
								cb.call(this, true);

								return;
							}

							// This actually fails the player, we just pretend to push the crate as FourArms to show that the crate can't be moved past a wall.
							this.mesh.spine.state.setAnimation(0, 'push', true);
							this._pushingCrate = true;
							P3.utils.delayg(this.tweens, 0.2, () => {
								this.tweens.removeAll();
								P3.audio.playSound('sfx_bump_into_block');
								P3.utils.delayg(this.tweens, 1, () => {
									this.onDeath('passive');
								});
							});
							cb.call(this, false);

							return;
						} else if (this.currentCharacter === 'ben') {
							P3.utils.delay(0.5, () => {
								P3.audio.playSound('sfx_bump_into_block');

								this.mesh.spine.state.setAnimation(0, 'walk_bump', false);
							});

							cb.call(this, false);
						}

						cb.call(this, false);

						return;
					}
					// if we're already moving the box we need to increase speed because it needs to move further
					const time = crate.isMoving ? this.aliendata.walkspeed * 1 : this.aliendata.walkspeed;

					if (!this.mesh.spine.state.tracks[0].animation.name.includes('push')) {
						this.mesh.spine.state.setAnimation(0, 'push', true);
					}

					this._pushingCrate = true;

					crate.activate({action: 'push', nextTile: nextTile, secondTile: secondTile, delay: 0, time: time});
				} else if (crate && type === 'jump') {
					if (midjump || this.currentCharacter !== 'cannonbolt') {
						cb.call(this, false);
					} else {
						crate.activate({action: 'smash', delay: this.aliendata.walkspeed * 0.5});

						cb.call(this, true);
					}

					return;
				}
			}

			cb.call(this, true);
		} else if (this.currentCharacter === 'cannonbolt') {
			const state = this.mesh.spine.state;

			if (type === 'move' && state.tracks[0].animation.name !== 'roll' && nextTile.types.includes('acid')) {
				state.setAnimation(0, 'roll_start', false);
				state.tracks[0].listener = {
					complete: () => {
						state.setAnimation(0, 'roll', true);
						cb.call(this, true);
					}
				};
			} else {
				cb.call(this, true);
			}
		} else {
			cb.call(this, true);
		}
	}

	playCompleteAnim() {
		P3.audio.playSound('vo_ben_woohoo_00');

		const startAnim = Math.random() > 0.5 ? 'complete_1' : 'complete_2';
		const nextAnim = 'idle1';

		const spineState = this.mesh.spine.state;

		spineState.data.setMix(startAnim, nextAnim, 0.25);

		spineState.clearListeners();
		spineState.setAnimation(0, startAnim, false);
		spineState.addAnimation(0, nextAnim, true, 0);
	}

	onMove(newTile, type, cb = null) {
		this.tile.removeObject(this);

		newTile.addObject(this);
		this.tile = newTile;

		if (this.tile.types.includes('object') && !this.moving) {
			const objectTile = this.tile.objects.find((e) => {
				return e instanceof ObjectTile && e.objectType === ObjectTile.TERMINAL;
			});

			if (objectTile) {
				objectTile.activate(this, () => {
					if (!cb) {
						this.signals[type].dispatch(this);
					} else {
						cb();
					}
				});
			} else {
				if (!cb) {
					this.signals[type].dispatch(this);
				} else {
					cb();
				}
			}
		} else if (!this.tile.isTile || this.tile.isBad) {
			if (
				(this.tile.types.includes('acid') && this.aliendata.invincibleToAcid) ||
                (this.tile.types.includes('lava') && this.aliendata.invincibleToLava) ||
                (this.tile.types.includes('trap'))) {
				const trap = this.tile.objects.find((element) => {
					return element instanceof Trap;
				});

				if (!trap || !trap.activated) {
					if (!cb) {
						this.signals[type].dispatch(this);
					} else {
						cb();
					}

					return;
				}
			}

			this.tweens.removeAll();

			if (this.tile.types.includes('lava')) {
				P3.audio.playSound('sfx_die_lava_00');
			} else if (this.tile.objects.find((e) => {
				return e instanceof SpikeTrap;
			})) {
				P3.audio.playSound('sfx_die_spike_00');
			}

			P3.audio.playSound('vo_ben_hurt_00');

			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({
					z : this.previousTile.position3D.z,
					x : this.previousTile.position3D.x
				}, 400)
				.easing(P3.utils.TWEEN.Easing.Linear.None)
				.delay(50)
				.start();

			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({
					y: [this.mesh.position.y + 4, this.mesh.position.y]
				}, 400)
				.easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
				.interpolation(P3.utils.TWEEN.Interpolation.CatmullRom)
				.onStart(() => {
					this.mesh.spine.state.setAnimation(0, this.animations.jump, false);
				})
				.onComplete(() => {
					this.dead = true;
					this.onDeath('death');
				})
				.delay(50)
				.start();
		} else if (this.tile.types.includes('conveyor')) {
			if (this.aliendata.canFly) {
				if (!cb) {
					this.signals[type].dispatch(this);
				} else {
					cb();
				}

				return;
			}

			const conveyor = this.tile.objects.find((element) => {
				return element instanceof ConveyorBelt;
			});

			if (!conveyor || !conveyor.activated) {
				if (!cb) {
					this.signals[type].dispatch(this);
				} else {
					cb();
				}

				return;
			}

			// Update new tile
			const currentPos = Object.assign({}, this.tile.position);

			if (this.tile.types.includes('left') || this.tile.types.includes('right')) {
				currentPos.x += conveyor.direction.v;
			} else {
				currentPos.y += conveyor.direction.v;
			}

			// Tween to new tile
			const newTile = this.world.grid.getTileAt(currentPos.x, currentPos.y);

			this.rotate(conveyor.direction.camrot * PIXI.DEG_TO_RAD, conveyor.speed - 100, false, true, () => {
				this.preMove(newTile, (result) => {
					if (!result) {
						this.onDeath('passive');

						return;
					}

					new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
						.to({
							x : newTile.position3D.x,
							z : newTile.position3D.z
						}, conveyor.speed)
						.easing(P3.utils.TWEEN.Easing.Linear.None)
						.onStart(() => {
							this.moving = true;
							if (!this.mesh.spine.state.tracks[0].animation.name.includes('idle')) {
								this.mesh.spine.state.setAnimation(0, this.animations.idle, true);
							}
						})
						.onComplete(() => {
							this.moving = false;
							this.onMove(newTile, 'moved');
						})
						.start();
				}, true, 'move');
			});
		} else if (this.tile.types.includes('ice')) {
			if (this.aliendata.canFly) {
				this.signals[type].dispatch(this);

				return;
			}
			const currentPos = Object.assign({}, this.tile.position);

			if (this.direction === Player.DIRECTION.LEFT || this.direction === Player.DIRECTION.RIGHT) {
				currentPos.x += this.direction.dir;
			} else {
				currentPos.y += this.direction.dir;
			}

			const newTile = this.world.grid.getTileAt(currentPos.x, currentPos.y);

			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({
					x : newTile.position3D.x,
					z : newTile.position3D.z
				}, 500)
				.easing(P3.utils.TWEEN.Easing.Linear.None)
				.onStart(() => {
					this.moving = true;
				})
				.onComplete(() => {
					this.moving = false;

					this.onMove(newTile, 'moved');
				})
				.start();
		} else if (this.tile.isUnderwater && !this.tile.isWall && !this.isUnderwater) {
			const pos = this.tile._3DPosition;

			this.waterParticles.position.set(pos.x, pos.y + 7, pos.z);
			this.waterParticles.playDuration(0.8, false);

			if (this.aliendata.canFly) {
				if (!cb) {
					this.signals[type].dispatch(this);
				} else {
					cb();
				}

				return;
			}

			const tweenDest = this.aliendata.invincibleToWater ? this.tile.position3D.y + 0.4 : this.mesh.position.y - 4;
			const tweenEasing = this.aliendata.invincibleToWater ? P3.utils.TWEEN.Easing.Linear.None : P3.utils.TWEEN.Easing.Back.Out;

			const swimUpTween = new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({y: '+2'}, 2000)
				.easing(P3.utils.TWEEN.Easing.Back.InOut)
				.onStart(() => {
					if (this.currentCharacter === 'ben') {
						this.mesh.spine.state.setAnimation(0, 'surface_water', true);
					}
				})
				.onComplete(() => {
					return this.onDeath('instantDeath');
				});

			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({y: tweenDest}, 1000)
				.easing(tweenEasing)
				.onStart(() => {
					if (!this.aliendata.invincibleToWater && (this.mesh.spine.skeleton.data.findAnimation('fall_water') !== null)) {
						P3.audio.playSound('sfx_die_water_00');

						this.mesh.spine.state.setAnimation(0, 'fall_water', true);
					} else {
						// TODO: we need water falling animations for other aliens too?
						this.mesh.spine.state.setAnimation(0, 'idle', true);
					}

					if (this.currentCharacter === 'overflow') {
						P3.audio.playSound('sfx_overflow_enterwater_00');
					}
				})
				.onComplete(() => {
					if (!this.aliendata.invincibleToWater) {
						swimUpTween.start();
					} else {
						if (!cb) {
							this.signals[type].dispatch(this);
						} else {
							cb();
						}
					}
				})
				.start();
		} else if (this.tile.types.includes('particles')) {
			const tile = this.tile.objects.find((e) => {
				return e instanceof ParticleTile;
			});

			if (this.aliendata.dangerousParticles.includes(tile.jsonName) && !this.isUnderwater) {
				this.onDeath('death');
			} else {
				if (!cb) {
					this.signals[type].dispatch(this);
				} else {
					cb();
				}
			}
		} else {
			if (this.tile.isGoal) {
				return;
			}

			if (!cb) {
				this.signals[type].dispatch(this);
			} else {
				cb();
			}
		}
	}

	destroy() {
		this.destroySignals();
	}

	update() {
		if (this.mesh.spine) {
			this.mesh.spine.update(P3.Ticker.dt);
		}

		if (this.tweens) {
			this.tweens.update();
		}
	}

	setTilePosition(x, y) {
		const newTile = this.world.grid.getTileAt(x, y);

		this.mesh.position.set(newTile.position3D.x, newTile.position3D.y + 2.15, newTile.position3D.z);
		this.mesh.rotation.set(0, 0, 0, 'XYZ');
		this.tile.removeObject(this);
		this.tile = newTile;
		this.tile.addObject(this);
	}

	/**
     * Rotates the player at a given amount. Y axis only.
	*/
	rotate(amount, speed, signal = true, to = false, callback = null) {
		let dest = to ? amount : this.mesh.rotation.y + amount;

		if (this.mesh.rotation.y === dest) {
			if (callback) {
				callback();
			}

			if (signal) {
				this.signals.rotated.dispatch(this);
			}

			return;
		}

		let rotInDeg = Math.floor((dest * PIXI.RAD_TO_DEG) + 0.5);

		if (Math.abs(rotInDeg) > 360) {
			rotInDeg %= 360;
		}

		const currRotation = this.mesh.rotation.y;

		if (dest * PIXI.RAD_TO_DEG === 270 && currRotation >= 0) {
			dest = -90 * PIXI.DEG_TO_RAD;
		}

		new P3.utils.TWEEN.Tween(this.mesh.rotation, this.tweens)
			.to({
				y: dest
			}, speed)
			.onStart(() => {
				this.rotating = true;

				switch (rotInDeg) {
					case 0: case 360:
						this.direction = Player.DIRECTION.RIGHT; break;
					case -270: case 90:
						this.direction = Player.DIRECTION.UP; break;
					case -180: case 180:
						this.direction = Player.DIRECTION.LEFT; break;
					case -90: case 270:
						this.direction = Player.DIRECTION.DOWN; break;
					default:
						this.direction = Player.DIRECTION.RIGHT;
						console.error('[Player.rotate()] ROTATION TWEEN: Could not sort rotation into category.');
						common.t.track('game error event', {
							gamelevel     : common.currentLevel,
							gameerror     : 1,
							gameerrorcode : 'Player rotation tween could not be sorted into category',
							gamecharacter : this.currentCharacter
						});
						break;
				}
			})
			.onUpdate(() => {
				common.world.hideObjects();
			})
			.onComplete(() => {
				this.rotating = false;

				this.mesh.rotation.y = rotInDeg < 0 ? (rotInDeg + 360) * PIXI.DEG_TO_RAD : rotInDeg * PIXI.DEG_TO_RAD;

				if (callback) {
					callback();
				}

				if (signal) {
					this.signals.rotated.dispatch(this);
				}
			})
			.easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
			.start();
	}

	/**
     * Moves the player linearly in dir, over dist in speed.
     * @param dir   {Object}   	The direction to go in.
     * @param dist  {Number}    The distance to travel.
     * @param speed {Number}    The speed to travel at in ms.
	 */
	move(dir = Player.DIRECTION.RIGHT, dist = 1, speed = 300) {
		this.previousTile = this.tile;

		let newTile = null;

		if (dir.name === 'left' || dir.name === 'right') {
			newTile = this.world.grid.getTileAt(this.tile.position.x + (dist * dir.dir), this.tile.position.y);
		} else {
			newTile = this.world.grid.getTileAt(this.tile.position.x, this.tile.position.y + (dist * dir.dir));
		}

		const acidTile = newTile.types.includes('acid');

		this.preMove(newTile, (result) => {
			if (!result) {
				this.onDeath('passive');

				return;
			}

			new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
				.to({
					z : newTile.position3D.z,
					x : newTile.position3D.x
				}, speed)
				.easing(P3.utils.TWEEN.Easing.Linear.None)
				.onStart(() => {
					let anim = this._pushingCrate ? 'push' : this.animations.run;

					if (this.currentCharacter === 'cannonbolt' && acidTile) {
						anim = 'roll';
					}

					this.footstep++;
					this.moving = true;

					if (!this.mesh.spine.state.tracks[0].animation.name.includes(anim)) {	// if we're already playing that anim, dont do it again (causes twitching)
						this.mesh.spine.state.setAnimation(0, anim, true);
					}

					this.mesh.spine.state.tracks[0].listener =
                    {
                    	event: () => {
                    		const sound = this.footstep % 2 === 0 ? this.aliendata.footstepsLeft[common.environment] : this.aliendata.foorstepsRight[common.environment];

                    		P3.audio.playSound(sound);
                    	}
                    };
				})
				.onUpdate(() => {
					common.world.hideObjects();
				})
				.onComplete(() => {
					this._pushingCrate = false;
					this.moving = false;
					this.onMove(newTile, 'moved');
				})
				.start();
		}, true, 'move');
	}

	/**
     * Makes the player jump up and move back down and travel a distance.
     * @param dir       {Player.DIRECTION}    			The direction to go in.
     * @param dist      {Number}                        The distance to travel.
     * @param height    {Number}                        The height to jump.
     * @param speed     {Number}                        The speed to travel at in ms.
	 */
	jump(dir = Player.DIRECTION.RIGHT, dist = 2, height = 4, speed = 300) {
		dir = this.direction;
		this.previousTile = this.tile;
		let newTile = null;
		let jumpedTile = null;	// we're gonna have to perform a check to see if the tile we're jumping over is a wall, so we don't jump into and through it.

		if (dir === Player.DIRECTION.RIGHT || dir === Player.DIRECTION.LEFT) {
			newTile = this.world.grid.getTileAt(this.tile.position.x + (dist * dir.dir), this.tile.position.y);
			jumpedTile = this.world.grid.getTileAt(this.tile.position.x + Number(dir.dir), this.tile.position.y);
		} else {
			newTile = this.world.grid.getTileAt(this.tile.position.x, this.tile.position.y + (dist * dir.dir));
			jumpedTile = this.world.grid.getTileAt(this.tile.position.x, this.tile.position.y + Number(dir.dir));
		}

		this.preMove(jumpedTile, (result) => {
			if (!result) {
				this.onDeath('passive');

				return;
			}

			this.preMove(newTile, (result) => {
				if (!result) {
					this.onDeath('passive');

					return;
				}

				const crates = newTile.objects.filter((element) => {
					return element instanceof CrateObject;
				});

				const isCrate = crates.length > 0;

				new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
					.to({
						z : newTile.position3D.z,
						x : newTile.position3D.x
					}, speed)
					.easing(P3.utils.TWEEN.Easing.Linear.None)
					.onStart(() => {
						if (this.currentCharacter === 'ben') {
							P3.audio.playSound('vo_ben_jump_00');
						}

						this.moving = true;
						this.mesh.spine.state.setAnimation(0, this.currentCharacter === 'cannonbolt' && isCrate ? 'jump_3' : this.animations.jump, false);
					})
					.onUpdate(() => {
						common.world.hideObjects();
					})
					.onComplete(() => {
						if (this.currentCharacter === 'ben') {
							P3.audio.playSound('vo_ben_land_heavy_00');
						}

						this.moving = false;
						this.onMove(newTile, 'jumped');

						const sounds = ['sfx_footstep_metal_00', 'sfx_footstep_temple_00', 'sfx_footstep_stone_00'];
						const sound = sounds[common.environment];

						P3.audio.playSound(sound);
					})
					.start();

				new P3.utils.TWEEN.Tween(this.mesh.position, this.tweens)
					.to({
						y: [this.mesh.position.y + height, this.mesh.position.y]
					}, speed)
					.easing(P3.utils.TWEEN.Easing.Sinusoidal.InOut)
					.interpolation(P3.utils.TWEEN.Interpolation.Bezier)
					.start();
			}, true, 'jump', false);
		}, false, 'jump', true);
	}

	setSkin(who) {
		if (this.currentCharacter === who) {
			return;
		}

		this.currentCharacter = who;
		this.aliendata = aliendata[who];

		const characterdata = this.animationData[this.currentCharacter];

		this.animations = this.aliendata.animations;
		this._walkspeed = characterdata.speed;
		this._canSwim = characterdata.canswim;
		this._walkspeedSpecial = characterdata.special_speed;
		this.name = characterdata.printname;

		this.mesh.spine.parent.remove(this.mesh.spine);
		this.mesh.spine = null;
		this.mesh.spine = this.spineStates[this.currentCharacter].spine;
		this.mesh.add(this.mesh.spine);

		// this.mesh.spine.state.setAnimation(0, this.animations.idle, true);
		this.mesh.spine.autoUpdate = false;
		this.mesh.spine.update(0);
	}

	transform(who = 'ben', signal = true, cb = null, deathCheck = true) {
		if (this.currentCharacter === who) {	// no need to transform
			if (cb) {
				cb();
			}

			if (signal) {
				this.signals.transform.dispatch(this);
			}

			return;
		}

		if (this.currentCharacter !== 'ben' && who !== 'ben') {
			new Promise((resolve) => {
				this.aliendata.deactivate(this, this.world, resolve);
			}).then(() => {
				this.setSkin('ben');

				this.signals.transformEffect.dispatch(0, false);

				this.mesh.spine.state.setAnimation(0, 'transform_back', true);

				this.mesh.spine.state.tracks[0].listener = {
                	complete: () => {
                		this.transform(who, signal, cb);
                	}
				};
			});

			return;
		}

		new Promise((resolve) => {
			if (this.currentCharacter === 'ben') {
				this.aliendata.transform(this, resolve);
			} else {
				this.aliendata.deactivate(this, this.world, resolve);
			}
		}).then(() => {
			if (who === 'ben') {
				P3.audio.playSound('sfx_omnitrix_transform_back_01');
			}

			this.setSkin(who);

			new Promise((resolve) => {
				this.aliendata.ability(this, this.world, resolve);
			}).then(() => {
				const next = () => {
					if (deathCheck) {
						if (this.tile.isBad && this.currentCharacter === 'ben') {
							this.onDeath('instantDeath');
							return;
						} else if (this.tile.isUnderwater && this.isUnderwater && !this.aliendata.invincibleToWater) {
							this.onDeath('instantDeath');
							return;
						}
					}

					if (cb) {
						cb();
					}

					if (signal) {
						this.signals.transform.dispatch(this);
					}
				};

				if (who === 'ben') {
					this.signals.transformEffect.dispatch(0);

					this.mesh.spine.state.setAnimation(0, 'transform_back', false);
					this.mesh.spine.state.tracks[0].listener = {
                    	complete: next
					};
				} else {
					next();
				}
			});
		});
	}

	get canSwim() {
		return this._canSwim;
	}

	get isUnderwater() {
		return this._isUnderwater;
	}

	set isUnderwater(v) {
		this._isUnderwater = v;
	}
}

Player.DIRECTION = {
	LEFT: {
		dir  : -1,
		name : 'left'
	},
	RIGHT: {
		dir  : 1,
		name : 'right'
	},
	UP: {
		dir  : -1,
		name : 'up'
	},
	DOWN: {
		dir  : 1,
		name : 'down'
	}
};
